import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Calendar } from 'lucide-react';
import { format, parse, isValid, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

const Wrap = styled.div`
  position: relative;
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
  width: 100%;
`;

const TextInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: ${(p) => p.theme.spacing.md};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.md};
  font-size: ${(p) => p.theme.fontSizes.base};
  transition: border-color ${(p) => p.theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.colors.primary};
  }

  &:disabled {
    background: ${(p) => p.theme.colors.gray50};
    cursor: not-allowed;
  }
`;

const PickerBtn = styled.button`
  flex-shrink: 0;
  padding: 0 ${(p) => p.theme.spacing.md};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius.md};
  background: ${(p) => p.theme.colors.white};
  color: ${(p) => p.theme.colors.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color ${(p) => p.theme.transitions.fast}, color ${(p) => p.theme.transitions.fast};

  &:hover:not(:disabled) {
    border-color: ${(p) => p.theme.colors.primary};
    color: ${(p) => p.theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HiddenDate = styled.input.attrs({ type: 'date' })`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  opacity: 0;
  pointer-events: none;
`;

/** Up to 8 digits → dd/mm/yyyy (e.g. 04041997 → 04/04/1997) */
function formatDigitsToDDMMYYYY(digits) {
  const d = digits.replace(/\D/g, '').slice(0, 8);
  if (d.length === 0) return '';
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/**
 * Shows and accepts dates as dd/mm/yyyy; stores yyyy-MM-dd via onChange (API-friendly).
 * Calendar button opens the native date picker. Pass `hidePicker` where the
 * field sits in a tight column and typing is the expected input — the button
 * costs ~52px of width. Typed entry is unaffected either way.
 */
const DateInputDDMMYYYY = ({
  id,
  value,
  onChange,
  onCommit,
  min,
  max,
  required,
  disabled,
  hidePicker = false,
  placeholder = 'dd/mm/yyyy',
  'aria-label': ariaLabel
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const hiddenRef = useRef(null);

  useEffect(() => {
    if (!value) {
      setText('');
      return;
    }
    try {
      const d = parseISO(value);
      setText(isValid(d) ? format(d, 'dd/MM/yyyy') : '');
    } catch {
      setText('');
    }
  }, [value]);

  const openPicker = () => {
    if (disabled) return;
    try {
      hiddenRef.current?.showPicker?.();
    } catch {
      hiddenRef.current?.click?.();
    }
  };

  const handleHiddenChange = (e) => {
    const iso = e.target.value;
    onChange?.(iso);
    if (iso) {
      const d = parseISO(iso);
      if (isValid(d)) setText(format(d, 'dd/MM/yyyy'));
    } else {
      setText('');
    }
    onCommit?.(iso);
  };

  const handleTextChange = (e) => {
    const display = formatDigitsToDDMMYYYY(e.target.value);
    setText(display);

    const digits = display.replace(/\D/g, '');
    if (digits.length === 0) {
      if (value) onChange?.('');
      return;
    }
    if (digits.length === 8) {
      const d = parse(display, 'dd/MM/yyyy', new Date());
      if (isValid(d)) {
        const iso = format(d, 'yyyy-MM-dd');
        if (iso !== value) onChange?.(iso);
      }
    }
  };

  const handleTextBlur = () => {
    const trimmed = text.trim();
    if (!trimmed) {
      if (value) onChange?.('');
      onCommit?.('');
      return;
    }
    const d = parse(trimmed, 'dd/MM/yyyy', new Date());
    if (!isValid(d)) {
      if (value) {
        const vd = parseISO(value);
        setText(isValid(vd) ? format(vd, 'dd/MM/yyyy') : '');
      } else {
        setText('');
      }
      return;
    }
    const iso = format(d, 'yyyy-MM-dd');
    if (iso !== value) onChange?.(iso);
    setText(format(d, 'dd/MM/yyyy'));
    onCommit?.(iso);
  };

  return (
    <Wrap>
      <TextInput
        id={id}
        value={text}
        onChange={handleTextChange}
        onBlur={handleTextBlur}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoComplete="off"
        inputMode="numeric"
        aria-label={ariaLabel || placeholder}
      />
      {!hidePicker && (
        <PickerBtn type="button" onClick={openPicker} disabled={disabled} aria-label={t('dateInput.openCalendar')}>
          <Calendar size={20} />
        </PickerBtn>
      )}
      <HiddenDate
        ref={hiddenRef}
        value={value || ''}
        min={min}
        max={max}
        onChange={handleHiddenChange}
        tabIndex={-1}
        aria-hidden="true"
      />
    </Wrap>
  );
};

export default DateInputDDMMYYYY;
